import type { WorkflowRunPeriod } from '@UI/Pages/WorkflowRuns/Filters/WorkflowRunFilterView';

export type PeriodBounds = { readonly startedFrom: Date | null; readonly startedTo: Date | null };

function startOfDay(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

function endOfDay(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

function shiftedDayStart(date: Date, dayOffset: number): Date {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate() + dayOffset, 0, 0, 0, 0);
}

/**
 * Bounds a preset to local calendar days, from a `now` the caller reads at choice time. `custom`
 * carries no bounds of its own here: switching to it keeps whatever free range is already stored,
 * which only `setCustomRange` writes. `startOfDay`/`endOfDay`/`shiftedDayStart` stay private: this
 * function is the single place that owns the day-boundary math, callers that need an end-of-day value
 * for a single date go through it (`resolvePeriodBounds('today', date).startedTo`) rather than
 * recomputing it.
 */
const resolvePeriodBounds = (period: WorkflowRunPeriod, now: Date): PeriodBounds => {
	switch (period) {
		case 'today':
			return { startedFrom: startOfDay(now), startedTo: endOfDay(now) };
		case 'last7Days':
			return { startedFrom: shiftedDayStart(now, -6), startedTo: endOfDay(now) };
		case 'last30Days':
			return { startedFrom: shiftedDayStart(now, -29), startedTo: endOfDay(now) };
		case 'all':
		case 'custom':
			return { startedFrom: null, startedTo: null };
	}
};

export default resolvePeriodBounds;
